import { useEffect, useState } from "react";
import type { MpRoomState } from "../multiplayer/types";
import type { RemotePosition } from "../multiplayer/useMpRoom";

interface Props {
  state: MpRoomState;
  positions: Map<string, RemotePosition>;
  myId: string;
  courseLength: number;
  /** Local player's current arc-length position. */
  myDistance: number;
}

interface Row {
  id: string;
  name: string;
  shirt: string;
  isMe: boolean;
  finished: boolean;
  distance: number;
  pct: number;
}

/**
 * In-race standings widget: floating top-left card showing each player's
 * progress along the course as a coloured bar + rank. Updates every 200 ms.
 */
export function RaceStandings({ state, positions, myId, courseLength, myDistance }: Props) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = window.setInterval(() => setTick((n) => n + 1), 200);
    return () => window.clearInterval(t);
  }, []);
  void tick;

  const rows: Row[] = state.players.map((p) => {
    const isMe = p.id === myId;
    const distance = isMe ? myDistance : (positions.get(p.id)?.s ?? 0);
    const pct = Math.max(0, Math.min(100, (distance / Math.max(1, courseLength)) * 100));
    return {
      id: p.id,
      name: p.displayName.slice(0, 12),
      shirt: (p.avatarConfig?.shirtColor as string | undefined) ?? "#ff5fa7",
      isMe,
      finished: p.finished,
      distance,
      pct,
    };
  });

  rows.sort((a, b) => {
    if (a.finished !== b.finished) return a.finished ? -1 : 1;
    return b.distance - a.distance;
  });

  return (
    <div className="race-standings" aria-hidden data-touch-passthrough>
      {rows.map((r, idx) => (
        <div key={r.id} className={`race-row ${r.isMe ? "is-me" : ""}`}>
          <span className="race-rank">{idx + 1}</span>
          <span className="race-dot" style={{ background: r.shirt }} />
          <span className="race-name">{r.name}{r.finished ? " ✓" : ""}</span>
          <div className="race-bar">
            <div className="race-bar-fill" style={{ width: `${r.pct}%`, background: r.shirt }} />
          </div>
        </div>
      ))}
    </div>
  );
}
